import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { UILog } from "@dto/log.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { parseJSON } from "@dto/util/util";
import axios, { AxiosError, HttpStatusCode } from "axios";
import clc from 'cli-color';
import { ReactNode } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { API_URL } from "src/data/Api";
import { TextUtil } from "src/util/TextUtil";
import { postApi } from "src/util/apiCalls";
import { GLOB } from "../util/Glob";
import { RingBuffer } from "../util/ring-buffer";

type ErrorDetail = Error | AxiosError | TypeError | string | object;

/**
 * Static methods for logging
 */
export abstract class Log {

  private static readonly STORAGE_KEY = 'uiErrors';

  private static readonly lastWarn: {
    timestamp: number,
    msg;
  } = { msg: '', timestamp: 0 };

  private static counter = 0;

  /**
   * Errors already shown to the user, to prevent duplicate notifications
   * when both a caller and the axios interceptor fallback report the same error
   */
  private static readonly notifiedErrors = new WeakSet<object>();

  static readonly ERRORS = Log.initErrorBuffer();
  static readonly STATIC_NOTIFICATION_KEY = 'staticNotificationKey';

  private static initErrorBuffer() {
    const rb = new RingBuffer(19);
    const storedJson = sessionStorage.getItem(Log.STORAGE_KEY);
    if (storedJson) {
      rb.fromArray(parseJSON(storedJson, []));
    }
    return rb;
  }

  /**
   * Notifies the user with a success message, duration 2s
   * @param message ReactNode to display
   */
  static success(message: ReactNode) {
    GLOB.app.notification.success({
      title: "Success",
      duration: 2,
      description: message,
      className: "success-notification",
      style: {
        color: "darkgreen",
      },
    });
  }

  private static isForbidden(detail: ErrorDetail) {
    return axios.isAxiosError(detail) && detail.response?.status === HttpStatusCode.Forbidden;
  }

  /**
   * Do NOT use directly, sends error to log on server
   * @param log obj to log
   */
  static sendToBE(log: UILog) {
    postApi<BasicResponseDTO, UILog>(API_URL.LOG_ERR, log).catch((reason: AxiosError) => console.error('Failed to store log on server!', reason, log));
  }

  private static addInfo(detail: object) {
    const be = useAppGlobStore.getState().info?.backend;
    return {
      ...detail,
      xormon: be?.version,
      date: DateUtil.getDateISO(),
    };
  }

  /**
   * Notifies the user with an error message
   * @param message ReactNode to display
   * @param detail object to show in modal on click
   * @returns
   */
  static error(message: ReactNode, detail?: ErrorDetail) {

    if (!GLOB.authService.isLoggedIn() || axios.isCancel(detail)) return;

    if (axios.isAxiosError(detail)) {
      if (Log.notifiedErrors.has(detail)) return;
      Log.notifiedErrors.add(detail);
    }

    let detailString = '';

    if (detail) {
      if (axios.isAxiosError(detail) && detail.response) {
        detailString = JSON.stringify(Log.addInfo(detail.response), TextUtil.hideKeysReplacer, 2);
      } else if (detail instanceof TypeError) {
        detailString = TextUtil.formatJSON(Log.addInfo(detail), Object.getOwnPropertyNames(detail));
      } else if (typeof detail === 'object') {
        detailString = JSON.stringify(Log.addInfo(detail), TextUtil.hideKeysReplacer, 2);
      } else {
        detailString = detail;
      }
    }

    Log.ERRORS.add(clc.red('[Error] ') + DateUtil.getDateISO() + ' ' + clc.red(message) + detailString);
    sessionStorage.setItem(Log.STORAGE_KEY, TextUtil.formatJSON(Log.ERRORS.toArray()));

    const key = Log.counter++ + '';

    GLOB.app.notification.error({
      key: key,
      title: (this.isForbidden(detail) ? 'Forbidden' : 'Error') + (detail ? ' (click for detail)' : ''),
      duration: 5,
      description: message,
      className: "error-notification",
      style: {
        //color: "red",
        cursor: detail ? 'pointer' : 'auto'
      },
      //duration: 99999999,
      onClick: () => {
        if (detail) {
          GLOB.app.notification.destroy(key);
          GLOB.app.modal.info({
            title: 'Error detail',
            icon: null,
            centered: true,
            width: '80%',
            closable: true,
            mask: { closable: true },
            className: "xm-notification-detail-modal",
            okButtonProps: { style: { display: 'none' } },
            content: <div className="xm-notification-detail">{message}<pre>{detailString}</pre></div>
          });
        }
      },
    });

    //Log.sendToBE({ level: "error", log: detail });
  }

  /**
   * Notifies the user with an info message
   * @param message ReactNode to display
   * @param duration default 3s
   * @returns
   */
  static info(message: ReactNode, duration = 3) {
    GLOB.app.notification.info({
      title: "Info",
      duration,
      description: message,
      className: "info-notification",
    });
  }

  /**
   * Notifies the user with a warning message, but only if the same message was not shown in the last second
   * @param message ReactNode to display
   * @param duration default 4s, 0 = infinite
   * @returns
   */
  static warn(message: ReactNode, duration = 4) {
    if (Log.lastWarn.timestamp + 1000 > Date.now() && Log.lastWarn.msg == message) return;
    Log.lastWarn.timestamp = Date.now();
    Log.lastWarn.msg = message;
    GLOB.app.notification.warning({
      key: duration === 0 ? Log.STATIC_NOTIFICATION_KEY : undefined,
      title: "Warning",
      duration,
      description: message,
      className: "warning-notification",
    });
  }

  /**
   * Relays to {@link console.log} if not production, {@link console.debug} otherwise
   * @param params
   * @returns
   */
  static debug(...params: unknown[]) {
    if (GLOB.isProdBuild() && GLOB.isProdEnv()) {
      console.debug(...params);
      return;
    }
    console.log(...params);
  }
}

// undefined = off, [] = all, [type1, type2] = only selected
// For autocomplete add category to ConsoleTagType
const allowed_types: (ConsoleTagType | (string & {}))[] | undefined = undefined;

console.tag = (type, ...msg) => {
  let printAllowed = true;
  if (typeof msg[msg.length - 1] === 'boolean')
    printAllowed = msg.pop() as boolean;

  if (!allowed_types || !printAllowed || (allowed_types.length && !allowed_types.includes(type)))
    return;

  console.debug(`%c[${type}]%c`, `color: ${toColor(type)};`, "", ...msg);
};

const toColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 100%, 65%)`;
};

